import type { PartnerConfig } from "@expert/app-config/types";
import type { ServiceProvider } from "@expert/service-providers";

import { useExpertWorkspaceWebSocket } from "@expert/websocket";
import { createContext, useEffect, useState } from "react";

import { updateDispatcherPartner } from "@/analytics";
import { env } from "@/config";
import { type CreateSdkParams, createAgentSDK } from "@/utils/createAgentSdk";

import type { AgentSdk } from "../agentSdk";

import { EvlFailedToConnectError } from "../../errorTypes";
import { useStatelessChatCallbacks } from "../chat/chatEventCallbacks";
import { getStatelessCacheActions } from "../chat/getStatelessCacheActions";
import { useExpertUniversitySdk } from "./useExpertUniversitySdk";
import { useIsTrainingMode } from "./useIsTrainingMode";

export const AgentSdkContext = createContext<AgentSdk | undefined>(undefined);

export type AgentSdkProviderCreator = (params: CreateSdkParams) => Promise<AgentSdk>;

export interface AgentSdkLoaderProps {
    employeeNum: string | null;
    userFriendlyName?: string;
    serviceProvider: ServiceProvider;
    partnerConfig?: PartnerConfig;
    createContainer: (name: string) => Promise<HTMLElement>;
    onAuthRedirect?: VoidFunction;
    skip?: boolean;
    isTrainingModeAllowed?: boolean;
}

export type AgentSdkFailure = "none" | "general" | "evl-failed";

export interface AgentSdkLoaderResult {
    failureType: AgentSdkFailure;
    errorMessage?: string;
    loadingMessage?: string;
    sdk?: AgentSdk;
}

function getAudioProvider(serviceProvider: ServiceProvider, partnerConfig?: PartnerConfig) {
    return serviceProvider === "AMAZON_CONNECT" && partnerConfig?.features.medialess ? "CCP" : "EVL";
}

export function useAgentSdkLoader({
    employeeNum,
    userFriendlyName,
    serviceProvider,
    partnerConfig,
    createContainer,
    onAuthRedirect,
    skip = false,
    isTrainingModeAllowed = false,
}: AgentSdkLoaderProps): AgentSdkLoaderResult {
    const [isReady, setIsReady] = useState(false);
    const [errorInitializingMsg, setErrorInitializingMsg] = useState<string | undefined>(undefined);
    const [agentSdk, setAgentSdk] = useState<AgentSdk | undefined>(undefined);
    const [failureType, setFailureType] = useState<AgentSdkFailure>("none");
    const { dataKeysCache, chatMessagesCache } = getStatelessCacheActions();
    const chatEventCallbacks = useStatelessChatCallbacks();
    const webSocket = useExpertWorkspaceWebSocket();
    const isTrainingMode = useIsTrainingMode(isTrainingModeAllowed, agentSdk);
    const sdk = useExpertUniversitySdk(isTrainingMode, agentSdk);

    useEffect(() => {
        const fetchAgentSdk = async (): Promise<{
            agentSdk?: AgentSdk;
            errorInitializing?: string;
            failureType: AgentSdkFailure;
        }> => {
            try {
                const sdkRes = await createAgentSDK({
                    createContainer,
                    employeeNum,
                    userFriendlyName,
                    serviceProvider,
                    dataKeysCache,
                    webSocket,
                    chatMessagesCache,
                    chatEventCallbacks,
                    onAuthRedirect,
                });

                return { agentSdk: sdkRes, failureType: "none" };
            } catch (error) {
                if (error instanceof EvlFailedToConnectError) {
                    return { errorInitializing: error.message, failureType: "evl-failed" };
                }

                return {
                    errorInitializing: error instanceof Error ? error.message : JSON.stringify(error),
                    failureType: "general",
                };
            }
        };

        let ignore = false;
        if (!agentSdk && !skip) {
            setIsReady(false);

            fetchAgentSdk()
                .then((res) => {
                    if (!ignore) {
                        if (res.agentSdk) {
                            setAgentSdk(res.agentSdk);
                            setFailureType(res.failureType);
                            setIsReady(true);
                        } else if (res.errorInitializing) {
                            setFailureType(res.failureType);
                            setErrorInitializingMsg(res.errorInitializing);
                        }
                    }
                })
                .catch((ex: unknown) => {
                    if (!ignore) {
                        setErrorInitializingMsg(ex instanceof Error ? ex.message : JSON.stringify(ex));
                    }
                });
        }

        return () => {
            ignore = true;
        };
        // oxlint-disable-next-line exhaustive-deps
    }, [agentSdk, employeeNum, userFriendlyName, serviceProvider, createContainer, skip]);

    useEffect(() => {
        if (!sdk || !isReady || !!errorInitializingMsg) {
            return;
        }

        const [partner] = sdk.getPartners();
        updateDispatcherPartner(partner);
    }, [sdk, isReady, errorInitializingMsg]);

    // NOTE: This error message can be set via an async callback in the constructor. That way
    //       if any errors are thrown during an async initialization, we can catch them and display
    //       the message here
    if (failureType !== "none") {
        return { errorMessage: errorInitializingMsg, failureType };
    }

    if (!sdk || !isReady) {
        const message =
            env.channel === "voice"
                ? ` make sure you're connected to ${getAudioProvider(serviceProvider, partnerConfig)}.`
                : "";
        return { loadingMessage: `Getting things ready...${message}`, failureType: "none" };
    }

    return { sdk, failureType: "none" };
}
